import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useVendorAuth } from '../context/VendorAuthContext'
import { checkFieldsForProfanity } from '../helper/profanityFilter'

const BusinessRegistration = () => {
    const { isVendorAuthenticated, vendor, updateVendor } = useVendorAuth()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(false)

    const [error, setError] = useState("")

    const [success, setSuccess] = useState("")

    const [businessData, setBusinessData] = useState({
        vendor_name: vendor?.vendor_name || "",

        business_name: "",

        address: "",
        city: "",
        zipcode: "",

        phone_number: "",

        facebook_url: "",
        twitter_url: "",
        yelp_url: "",
    })

    // =========================================
    // Handle Input Changes
    // =========================================
    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        const len = digits.length;
        if (len === 0) return '';
        if (len < 3) return `(${digits}`;
        if (len === 3) return `(${digits})`;
        if (len <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
        return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBusinessData({
            ...businessData,
            [name]: name === 'phone_number' ? formatPhoneNumber(value) : value,
        })
    }

    // =========================================
    // Register Business
    // =========================================
    const handleBusinessRegister = async (e) => {
        e.preventDefault()

        const profanityError = checkFieldsForProfanity({
            "Business Name": businessData.business_name,
            "Address": businessData.address,
            "City": businessData.city,
        })
        if (profanityError) { setError(profanityError); return }

        try {

            setLoading(true)
            setError("")
            setSuccess("")

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/businesses-register`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        vendor_name: businessData.vendor_name,

                        business_name: businessData.business_name,

                        address: businessData.address,
                        city: businessData.city,
                        zipcode: businessData.zipcode,

                        phone_number: businessData.phone_number,

                        facebook_url: businessData.facebook_url || null,
                        twitter_url: businessData.twitter_url || null,
                        yelp_url: businessData.yelp_url || null,
                    }),
                }
            )

            const data = await response.json()

            if (!response.ok) {
                setError(data.error || "Business registration failed")
                return
            }

            if (data.vendor) updateVendor(data.vendor)

            navigate("/vendor-dashboard")

            console.log("Registered Business:", data)

            // Reset Form
            setBusinessData({
                vendor_name: "",

                business_name: "",

                address: "",
                city: "",
                zipcode: "",

                phone_number: "",

                facebook_url: "",
                twitter_url: "",
                yelp_url: "",
            })

        } catch (error) {

            console.error(error)

            setError("Server error. Please try again.")

        } finally {

            setLoading(false)

        }
    }

    return (
        <section className="business-registration py-80">
            <div className="container container-lg">

                {/* Header */}
                <div className="text-center mb-48">
                    <h2 className="mb-16">
                        Register Your Business
                    </h2>

                    <p className="text-gray-600">
                        Add your business to the platform and begin offering coupons.
                    </p>
                </div>

                <div className="row justify-content-center">
                    <div className="col-xl-8 col-lg-10">

                        <div className="border border-gray-100 rounded-16 p-40 bg-white">

                            {/* Disclaimer */}
                            <div className="d-flex align-items-start gap-16 mb-40 pb-32 border-bottom border-gray-100">
                                <span className="text-2xl text-warning-600 flex-shrink-0">
                                    <i className="ph-fill ph-warning" />
                                </span>
                                <p className="text-gray-600 mb-0">
                                    <span className="fw-semibold text-neutral-900">Legal Disclaimer: </span>
                                    If you register a business under your Vendor account that is not your business you can face legal action.
                                </p>
                            </div>

                            {/* Login Redirect */}
                            {!isVendorAuthenticated && (
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-16 mb-40 pb-32 border-bottom border-gray-100">

                                <div>
                                    <h5 className="mb-8">
                                        You must be a vendor to create a business with coupons.
                                    </h5>

                                    <p className="text-gray-500 mb-0">
                                        Login to create a business.
                                    </p>
                                </div>

                                <Link
                                    to="/vendor-login"
                                    className="btn btn-outline-main py-14 px-32 rounded-8"
                                >
                                    Vendor Login
                                </Link>

                            </div>
                            )}

                            {/* Registration Form */}
                            <form onSubmit={handleBusinessRegister}>

                                <div className="row gy-4">

                                    {/* Vendor Name */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium">
                                            Vendor Name
                                        </label>

                                        <input
                                            type="text"
                                            className="common-input border-gray-100 bg-gray-50"
                                            name="vendor_name"
                                            value={businessData.vendor_name}
                                            onChange={handleChange}
                                            placeholder="Enter Your Vendor Name"
                                            readOnly={isVendorAuthenticated}
                                            maxLength={35}
                                        />
                                        <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: businessData.vendor_name.length >= 35 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                            {businessData.vendor_name.length}/35
                                        </p>
                                    </div>

                                    {/* Business Name */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium">
                                            Business Name
                                        </label>

                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="business_name"
                                            value={businessData.business_name}
                                            onChange={handleChange}
                                            placeholder="Example: Fun Card Media"
                                            maxLength={35}
                                        />
                                        <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: businessData.business_name.length >= 35 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                            {businessData.business_name.length}/35
                                        </p>
                                    </div>

                                    {/* Address */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium">
                                            Address
                                        </label>

                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="address"
                                            value={businessData.address}
                                            onChange={handleChange}
                                            placeholder="123 Main Street"
                                        />
                                    </div>

                                    {/* City */}
                                    <div className="col-md-6">
                                        <label className="mb-8 fw-medium">
                                            City
                                        </label>

                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="city"
                                            value={businessData.city}
                                            onChange={handleChange}
                                            placeholder="City"
                                        />
                                    </div>

                                    {/* Zipcode */}
                                    <div className="col-md-6">
                                        <label className="mb-8 fw-medium">
                                            Zipcode
                                        </label>

                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="zipcode"
                                            value={businessData.zipcode}
                                            onChange={handleChange}
                                            placeholder="Zipcode"
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium">
                                            Phone Number
                                        </label>

                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="phone_number"
                                            value={businessData.phone_number}
                                            onChange={handleChange}
                                            placeholder="(555)-555-5555"
                                        />
                                        {businessData.phone_number && (
                                            <p className="mb-0 mt-4 d-flex align-items-center gap-6" style={{
                                                fontSize: 12,
                                                color: businessData.phone_number.replace(/\D/g, '').length === 10 ? "#10b981" : "#ef4444",
                                                fontWeight: 500,
                                            }}>
                                                <i className={`ph ${businessData.phone_number.replace(/\D/g, '').length === 10 ? "ph-check-circle" : "ph-warning-circle"}`} style={{ fontSize: 14 }} />
                                                {businessData.phone_number.replace(/\D/g, '').length === 10 ? "Valid phone number" : `${businessData.phone_number.replace(/\D/g, '').length}/10 digits`}
                                            </p>
                                        )}
                                    </div>

                                    {/* Facebook URL */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium d-flex align-items-center gap-8">
                                            <i className="ph-fill ph-facebook-logo" style={{ color: '#1877f2', fontSize: 18 }} />
                                            Facebook Profile URL
                                            <span className="text-gray-400 fw-normal" style={{ fontSize: 12 }}>(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="facebook_url"
                                            value={businessData.facebook_url}
                                            onChange={handleChange}
                                            placeholder="https://facebook.com/yourbusiness"
                                        />
                                    </div>

                                    {/* Twitter URL */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium d-flex align-items-center gap-8">
                                            <i className="ph-fill ph-twitter-logo" style={{ color: '#1da1f2', fontSize: 18 }} />
                                            Twitter Profile URL
                                            <span className="text-gray-400 fw-normal" style={{ fontSize: 12 }}>(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="twitter_url"
                                            value={businessData.twitter_url}
                                            onChange={handleChange}
                                            placeholder="https://twitter.com/yourbusiness"
                                        />
                                    </div>

                                    {/* Yelp URL */}
                                    <div className="col-12">
                                        <label className="mb-8 fw-medium d-flex align-items-center gap-8">
                                            <span style={{ fontWeight: 900, fontSize: 15, color: '#d32323', fontFamily: 'Georgia, serif', lineHeight: 1 }}>Y</span>
                                            Yelp Page URL
                                            <span className="text-gray-400 fw-normal" style={{ fontSize: 12 }}>(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input border-gray-100"
                                            name="yelp_url"
                                            value={businessData.yelp_url}
                                            onChange={handleChange}
                                            placeholder="https://yelp.com/biz/yourbusiness"
                                        />
                                    </div>

                                    {/* Alerts */}
                                    <div className="col-12">

                                        {error && (
                                            <div className="alert alert-danger">
                                                {error}
                                            </div>
                                        )}

                                        {success && (
                                            <div className="alert alert-success">
                                                {success}
                                            </div>
                                        )}

                                    </div>

                                    {/* Submit */}
                                    <div className="col-12 mt-16">
                                        <button
                                            type="submit"
                                            className="btn bg-main-800 text-white py-18 px-40 rounded-8"
                                            disabled={loading}
                                        >
                                            {loading
                                                ? "Creating Business..."
                                                : "Register Business"}
                                        </button>
                                    </div>

                                </div>

                            </form>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default BusinessRegistration